/* eslint-disable eslint-comments/disable-enable-pair */
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { GoogleMap } from "@react-google-maps/api";
import { logEvent } from "@src/appV2/lib/analytics";
import { LA_CENTER } from "@src/appV2/lib/GoogleMaps";
import { convertFromGoogleMapsLocation } from "@src/appV2/Location/utils";
import { GoogleMapCustomMarkerWrapper } from "@src/appV2/redesign/GeoLocation/GoogleMapCustomMarkerWrapper";
import { MapMarkerExpanded } from "@src/appV2/redesign/GeoLocation/MapMarkerExpanded";
import { MapMarkerLocation } from "@src/appV2/redesign/GeoLocation/MapMarkerLocation";
import { useWorkerLocations } from "@src/appV2/redesign/GeoLocation/useWorkerLocations";
import {
  DEFAULT_MAP_ZOOM,
  googleMapOptions,
} from "@src/appV2/redesign/ShiftDiscovery/MapView/constants";
import { ShiftDiscoveryMapViewMapControls } from "@src/appV2/redesign/ShiftDiscovery/MapView/MapControls";
import { useLocationFilter } from "@src/appV2/redesign/ShiftDiscovery/MapView/useLocationFilter";
import { MapViewWorkplaceClusterer } from "@src/appV2/redesign/ShiftDiscovery/MapView/WorkplaceClusterer/Clusterer";
import { type Worker } from "@src/appV2/Worker/api/types";
import { USER_EVENTS } from "@src/constants";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { GoogleMapErrorBoundary } from "../../GeoLocation/GoogleMapErrorBoundary";
import { useGetPlacementWorkplaces } from "../api/useGetPlacementWorkplaces";
import { getWorkplacePlacementsPath } from "../paths";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { DEFAULT_DISTANCE_FILTER_IN_MILES } from "../utils";
import { FiltersTopBar } from "./FiltersTopBar";
import { useGetPlacementFilters } from "./PlacementDetailsPage/hooks/useGetPlacementFilters";
import { useSearchRadiusCircle } from "./useSearchRadiusCircle";

interface PlacementMapViewProps {
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter?: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;
  showApplications: boolean;
  placementCandidateId: string;
}

export function PlacementMapView(props: PlacementMapViewProps) {
  const { worker, filter, setFilter, showApplications, placementCandidateId } = props;
  const history = useHistory();

  const { locationFilter, onChangeLocationFilter, onChangeLocationFilterAfterDistance } =
    useLocationFilter();

  const { workerLocation, deviceLocation, homeLocation, shouldRequestLocationPermission } =
    useWorkerLocations();

  const [map, setMap] = useState<google.maps.Map>();

  const nonEmptyFilter = Object.fromEntries(
    Object.entries(filter ?? {}).filter(([_, value]) => value)
  );
  const placementFilters = useGetPlacementFilters({
    filter: nonEmptyFilter,
    showApplications,
    worker,
    enabled: isDefined(placementCandidateId),
  });

  const memorizedGetParams = useMemo(() => {
    return {
      workerId: worker.userId,
      queryParams: {
        filter: {
          ...placementFilters,
          coordinates: locationFilter,
        },
      },
    };
  }, [locationFilter, placementFilters, worker]);

  const { data: workplaces, isLoading: isWorkplacesLoading } = useGetPlacementWorkplaces(
    memorizedGetParams,
    { staleTime: 60 * 1000, enabled: isDefined(locationFilter) }
  );

  // Calculate the distance filter for the radius circle
  const distanceInMiles = useMemo(() => {
    if (isDefined(filter?.distanceInMiles) && (filter.distanceInMiles?.$lt ?? 0) > 0) {
      return filter.distanceInMiles.$lt;
    }

    if (showApplications) {
      // Don't show circle when showing applications (no distance filter)
      return 0;
    }

    return DEFAULT_DISTANCE_FILTER_IN_MILES;
  }, [filter?.distanceInMiles, showApplications]);

  // Convert homeLocation to GeoLocation format for the circle center
  const homeLocationGeo = useMemo(() => {
    return homeLocation ? convertFromGoogleMapsLocation(homeLocation) : undefined;
  }, [homeLocation]);

  // Display the search radius circle on the map, centered at home location
  useSearchRadiusCircle({
    map,
    radiusInMiles: distanceInMiles,
    locationFilter: homeLocationGeo,
    enabled: !showApplications && isDefined(homeLocationGeo),
  });

  return (
    <Stack sx={{ position: "relative", flex: 1 }}>
      <GoogleMapErrorBoundary>
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
          }}
          options={googleMapOptions}
          onLoad={(map) => {
            setMap(map);
            const defaultLocation = workerLocation ?? LA_CENTER;
            map.setCenter(defaultLocation);
            onChangeLocationFilter(defaultLocation);
          }}
          onUnmount={() => {
            setMap(undefined);
          }}
          onCenterChanged={() => {
            onChangeLocationFilterAfterDistance(map?.getCenter());
          }}
        >
          {workerLocation && (
            <GoogleMapCustomMarkerWrapper position={workerLocation}>
              <MapMarkerLocation />
            </GoogleMapCustomMarkerWrapper>
          )}
          {workplaces ? (
            <MapViewWorkplaceClusterer
              workplaces={workplaces.data.map((workplace) => ({
                type: workplace.type,
                id: workplace.id,
                attributes: {
                  ...workplace.attributes,
                  rating: workplace.attributes.rating,
                },
                shiftsCount: workplace.attributes.placementsCount,
              }))}
              map={map}
            >
              {(workplace, position) => {
                const { rating } = workplace.attributes;
                const ratingValue = rating?.value ? rating?.value.toFixed(1) : undefined;
                return (
                  <GoogleMapCustomMarkerWrapper key={workplace.id} position={position}>
                    {workplace.shiftsCount > 0 ? (
                      <MapMarkerExpanded
                        count={workplace.shiftsCount}
                        label="job"
                        rating={ratingValue}
                        onClick={() => {
                          logEvent(USER_EVENTS.TAP_FACILITY_PIN, {
                            workplaceId: workplace.id,
                            rating: rating?.value,
                          });
                          history.push(
                            getWorkplacePlacementsPath(workplace.id, "placement-map-view")
                          );
                        }}
                      />
                    ) : undefined}
                  </GoogleMapCustomMarkerWrapper>
                );
              }}
            </MapViewWorkplaceClusterer>
          ) : undefined}
        </GoogleMap>
        <ShiftDiscoveryMapViewMapControls
          canCenterToDevice={
            (Boolean(deviceLocation) && Boolean(map)) || shouldRequestLocationPermission
          }
          canCenterToHome={Boolean(homeLocation) && Boolean(map)}
          isLoading={isWorkplacesLoading}
          onCenterToHome={() => {
            if (map && homeLocation) {
              map.panTo(homeLocation);
              map.setZoom(DEFAULT_MAP_ZOOM);
              onChangeLocationFilter(homeLocation);
            }
          }}
          onCenterToDevice={() => {
            if (!deviceLocation && shouldRequestLocationPermission) {
              return;
            }

            if (map && deviceLocation) {
              map.panTo(deviceLocation);
              map.setZoom(DEFAULT_MAP_ZOOM);
              onChangeLocationFilter(deviceLocation);
            }
          }}
          onZoomIn={() => {
            const currentZoom = map?.getZoom();
            if (map && currentZoom) {
              map.setZoom(currentZoom + 1);
            }
          }}
          onZoomOut={() => {
            const currentZoom = map?.getZoom();
            if (map && currentZoom) {
              map.setZoom(currentZoom - 1);
            }
          }}
        >
          <Box
            sx={{ pt: 4, pl: 4, "&::-webkit-scrollbar": { display: "none" }, overflowX: "auto" }}
          >
            <FiltersTopBar
              filter={filter}
              setFilter={setFilter}
              worker={worker}
              placementCandidateId={placementCandidateId}
              showBackground={false}
            />
          </Box>
        </ShiftDiscoveryMapViewMapControls>
      </GoogleMapErrorBoundary>
    </Stack>
  );
}
